import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import useLicenseApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import { getLicenseSeats, grantLicenseSeat, revokeLicenseSeat } from '../../services/licenseService';
import api from '../../services/api';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Tabs from '../../components/ui/Tabs';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import Button from '../../components/ui/Button';
import LicenseRuleDrawer from './LicenseRuleDrawer';

// İ-9 — TEK KAYDIRMA DataTable satırlarıdır: sayfa gövdesi taşmaz. Sarmalayıcı bu yüzden
// `min-height: 0` taşır (HrAnalyticsRecruitingPage.js:66-72 emsali); bu atlanırsa tablonun
// gövde kaydırması sayfaya döner ve iç içe scroll doğar.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

// İ-9 — DataTable gövde yüksekliğini varsayılan `calc(100vh - 280px)` ile sabitler. Koltuklar
// sekmesinde bu 280px'in ÜSTÜNE `Tabs` şeridi biner (10px+10px dolgu + ~20px satır + 2px çizgi
// + 20px margin ≈ 62px); telafi edilmezse taşma sayfaya döner. 340px iki tabloyu da karşılar —
// şerit henüz görünmezken (tek sekme) gövde yalnız 60px kısalır, sayfa yine kaydırmaz.
const TABLO_GOVDE_YUKSEKLIGI = 'calc(100vh - 340px)';

// İ-9 — "Koltuk Ver" şeridi ARTIK TABLONUN İÇİNDE (`DataTable.toolbarActions`, bkz.
// DataTable.js:222-235 yorumu ve AtsCandidatesPage/HrAnalyticsRecruitingPage emsali).
// Eskiden tablonun ÜSTÜNDE ayrı bir şeritti: hem "aksiyon düğmesi tablonun içinde olmalı"
// altın kuralını ihlal ediyordu hem de gövde yüksekliğine eklenmeyen 72px'lik bir şerit
// olarak iç içe scroll üretiyordu.
const SeatsToolbar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SelectWrapper = styled.div`
  width: 240px;
`;

// Koltuk durumu (ModuleSeatAssignment.Status) — backend enum'ıyla BİREBİR:
// 0 Active, 1 Overage (tolerans bandı, çalışır), 2 Blocked (tavan üstü, modülü AÇMAZ),
// 3 Released (iade edilmiş; varsayılan listede GÖRÜNMEZ, yalnız süzgeçle sorulur).
export const SEAT_STATUSES = [
  { value: 0, key: 'license.statusActive' },
  { value: 1, key: 'license.statusOverage' },
  { value: 2, key: 'license.statusBlocked' },
  { value: 3, key: 'license.statusReleased' },
];

export const seatStatusKey = (status) =>
  SEAT_STATUSES.find((s) => s.value === status)?.key || '';

// P4Kü-12 — koltuğu ELLE kaldırma YALNIZ Source = Manual (1) satırlarda açıktır: kural koltuğu
// 409 license.seatFromRule, örtük (rol/yetki tetiklemeli) koltuk 409 license.seatImplicit döner.
// Eskiden yalnız `fromRule` bayrağına bakılıyordu, örtük koltukta düğme açık kalıp gereksiz 409
// üretiyordu. `source` gelmeyen (eski) yanıtta bayrağa düşülür.
export const canRevokeSeat = (row) => {
  if (!row) return false;
  if (row.source === undefined || row.source === null) return !row.fromRule;
  return row.source === 1;
};

// Kü-13 — arama YALNIZ `firstName[like]` ile yapılıyordu: soyadıyla arayan yönetici hiçbir
// sonuç görmüyordu. Depodaki kişi seçici kalıbı `fullName[like]` + pageSize 20'dir
// (InterviewScheduleDialog.js:147, LeavePolicyUsersTab.js:135, BulkLeaveDetailsPage.js:216).
// `e.userId || e.id` düşüşü KORUNUR: koltuk uçları User.Id bekler, /v1/employees `id` alanı
// Employee.Id'dir (LicenseSeatGrantRequest yorumu Employee.Id düşmesini destekler).
const searchEmployees = async (term) => {
  const params = term ? { 'fullName[like]': term, pageSize: 20 } : { pageSize: 20 };
  const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
  const r = res.data?.data;
  const items = Array.isArray(r) ? r : (r?.data || []);
  return items.map((e) => ({ id: e.userId || e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim() }));
};

export default function LicensesPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const { columns: baseColumns, data, fetching, refetch } = useLicenseApi();

  const [activeTab, setActiveTab] = useState('modules');
  const [selectedModule, setSelectedModule] = useState(null);
  const [ruleDrawerModule, setRuleDrawerModule] = useState(null);

  const [seatsQueryParams, setSeatsQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [seatsData, setSeatsData] = useState([]);
  const [seatsFetching, setSeatsFetching] = useState(false);
  const [seatsPagination, setSeatsPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [grantUserId, setGrantUserId] = useState(null);

  // Koltuk (seat) listesi — getLicenseSeats(moduleCode, params) sayfalanır,
  // parseTableParams'ın ürettiği queryParams her değiştiğinde yeniden çekilir.
  useEffect(() => {
    if (!selectedModule) return;
    setSeatsFetching(true);
    getLicenseSeats(selectedModule.code, seatsQueryParams)
      .then((res) => {
        // api.js GET okumalarında reject ETMEZ; gerçek sinyal res.data.status'tur.
        if (res.data?.status === false) {
          setSeatsData([]);
          return;
        }
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setSeatsData(items);
        setSeatsPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => setSeatsData([]))
      .finally(() => setSeatsFetching(false));
  }, [selectedModule, seatsQueryParams]);

  const handleRowClick = useCallback((row) => {
    setSelectedModule(row);
    setActiveTab('seats');
    setSeatsQueryParams({ pageSize: 10, pageNumber: 1 });
  }, []);

  const handleOpenRule = useCallback((row) => {
    setRuleDrawerModule(row);
  }, []);

  const handleGrantSeat = useCallback(async () => {
    if (!selectedModule || !grantUserId) return;
    try {
      await grantLicenseSeat(selectedModule.code, { userId: grantUserId });
      setGrantUserId(null);
      setSeatsQueryParams((p) => ({ ...p }));
      refetch();
    } catch {
      // handled by interceptor (ör. license.seatUnavailable)
    }
  }, [selectedModule, grantUserId, refetch]);

  const handleRevokeSeat = useCallback(async (row) => {
    if (!selectedModule) return;
    try {
      await revokeLicenseSeat(selectedModule.code, row.userId || row.id);
      setSeatsQueryParams((p) => ({ ...p }));
      refetch();
    } catch {
      // handled by interceptor (ör. license.seatFromRule)
    }
  }, [selectedModule, refetch]);

  // Kü-12 — "Koltuklar" kalemi KALDIRILDI: satıra tıklamak (onRowClick) zaten aynı sekmeyi
  // açıyordu, menüde ikinci bir yol MÜKERRER aksiyondu. Menüde yalnız satır tıklamasıyla
  // ulaşılamayan iş (kapsam kuralı çekmecesi) kalır.
  const actionOptions = useMemo(() => {
    if (!canWrite('EM')) return [];
    return [
      { type: 'edit', label: t('license.rule'), onClick: handleOpenRule },
    ];
  }, [t, canWrite, handleOpenRule]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  const seatColumns = useMemo(() => [
    { name: 'userId', label: 'ID', options: { display: 'excluded', filter: false, sort: false } },
    {
      name: 'fullName', label: t('employee.fullName'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    { name: 'email', label: t('employee.email'), options: { filter: false, sort: false } },
    {
      name: 'fromRule', label: t('license.rule'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = seatsData[tableMeta.rowIndex];
          return row?.fromRule ? t('license.rule') : '-';
        },
      },
    },
    {
      // P4Kü-12 / spec §9 — DURUM kolonu + süzgeci: "bloke bekleyenler" ekranda görünür olmalı.
      // Süzgeç `filterOptions.filterKey` ile `status` olarak gider (parseTableParams'ın düz değer
      // dalı) ve backend'in `[FromQuery] int? status` parametresini besler.
      name: 'status', label: t('license.seatStatus'),
      options: {
        filter: true, sort: false, filterType: 'custom',
        filterOptions: {
          filterKey: 'status',
          display: (filterList, onChange, index, column) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('license.seatStatus')}</InputLabel>
              <Select
                value={filterList[index]?.[0] ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  filterList[index] = v === '' ? [] : [v];
                  onChange(filterList[index], index, column);
                }}
                inputProps={{ 'data-testid': 'seat-status-filter' }}
              >
                <MenuItem value=""><em>{t('common.all', { defaultValue: 'Tümü' })}</em></MenuItem>
                {SEAT_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={String(s.value)}>{t(s.key)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ),
        },
        customBodyRender: (_, tableMeta) => {
          const row = seatsData[tableMeta.rowIndex];
          if (!row) return '-';
          const anahtar = seatStatusKey(row.status);
          return anahtar ? t(anahtar) : '-';
        },
      },
    },
    {
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = seatsData[tableMeta.rowIndex];
          if (!canWrite('EM') || !row) return null;
          return (
            <OptionsMenu
              actions={[
                {
                  type: 'delete',
                  label: t('license.revokeSeat'),
                  onClick: handleRevokeSeat,
                  // İ-1 — KAPININ ADI `condition`'DIR. OptionsMenu (OptionsMenu.js:105-112)
                  // yalnız `type`, `label`, `onClick`, `icon`, `color`, `divider` ve
                  // `condition(row)` okur; burada eskiden `disabled` yazıyordu ve o alan
                  // HİÇBİR YERDE okunmadığı için kapı ÖLÜYDÜ: kural kapsamından gelen ya da
                  // örtük koltukta "Koltuğu Kaldır" tıklanabiliyor ve her seferinde 409
                  // (license.seatFromRule / license.seatImplicit) dönüyordu.
                  // `condition` deseni deponun standardıdır (AssetAssignmentsPage.js:107,
                  // FeedbackFormsTab.js:452, MyShiftSwapsPage.js:110-112 — 19 çağrı yeri).
                  condition: canRevokeSeat,
                },
              ]}
              row={row}
            />
          );
        },
      },
    },
  ], [t, seatsData, canWrite, handleRevokeSeat]);

  // Kü-1 — `filterList` / `onFilterConfirm` KALDIRILDI: DataTable ikisini de kabul ediyor ama
  // kurulu mui-datatables@4.3.0'a HİÇ geçirmiyor (DataTable.js:147-150 kendi yorumu), yani
  // `seatsFilterList` state'i tamamen boşa çalışıyordu. Afiş uygulanan filtreden türetilir.
  const handleSeatsTableChanged = useCallback((action, tableState) => {
    const params = parseTableParams(tableState, seatColumns);
    setSeatsQueryParams(params);
  }, [seatColumns]);

  const tabs = useMemo(() => {
    const list = [{ key: 'modules', label: t('license.title') }];
    if (selectedModule) list.push({ key: 'seats', label: `${selectedModule.name} — ${t('license.seats')}` });
    return list;
  }, [selectedModule, t]);

  // Kü-8 — `pageSize` DataTable'ın `rowsPerPageOptions` listesinden (10/20/50) bir değer
  // OLMALIDIR: `data.length` verildiğinde MUI TablePagination listede olmayan bir değerle
  // uyarı basıyor ve seçim kutusu boş görünüyordu. Modül sayısı küçük olduğu için hepsini tek
  // sayfada tutan EN KÜÇÜK yasal seçenek kullanılır.
  const modulePageSize = useMemo(
    () => [10, 20, 50].find((n) => data.length <= n) || 50,
    [data.length],
  );

  return (
    <Container>
      <LicenseRuleDrawer
        open={!!ruleDrawerModule}
        moduleCode={ruleDrawerModule?.code}
        onClose={() => setRuleDrawerModule(null)}
        onSaved={refetch}
      />

      {tabs.length > 1 && (
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      )}

      {activeTab === 'modules' && (
        <DataTable
          title={t('license.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={data.length}
          pageIndex={0}
          pageSize={modulePageSize}
          serverSide={false}
          tableBodyMaxHeight={TABLO_GOVDE_YUKSEKLIGI}
          onRowClick={handleRowClick}
        />
      )}

      {activeTab === 'seats' && selectedModule && (
        <DataTable
          title={`${selectedModule.name} — ${t('license.seats')}`}
          fetching={seatsFetching}
          columns={seatColumns}
          data={seatsData}
          totalRowCount={seatsPagination.totalRowCount}
          pageIndex={seatsPagination.pageIndex}
          pageSize={seatsPagination.pageSize}
          tableBodyMaxHeight={TABLO_GOVDE_YUKSEKLIGI}
          toolbarActions={canWrite('EM') ? (
            <SeatsToolbar>
              <SelectWrapper>
                <AsyncGenericSelect
                  label={t('license.grantSeat')}
                  value={grantUserId}
                  onChange={setGrantUserId}
                  onSearch={searchEmployees}
                />
              </SelectWrapper>
              <Button onClick={handleGrantSeat} disabled={!grantUserId}>
                {t('license.grantSeat')}
              </Button>
            </SeatsToolbar>
          ) : null}
          onTableChanged={handleSeatsTableChanged}
        />
      )}
    </Container>
  );
}
